import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useToast } from './Toast';
import { useAuth } from '../auth/AuthContext';
import { extractErrorMessage } from '../utils/errors';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// ── Types ─────────────────────────────────────────────────────────────────────

type VmPowerState = 'Running' | 'Stopped' | 'Deallocated' | 'Starting' | 'Stopping' | 'Deallocating' | 'Unknown';
type VmPowerAction = 'Start' | 'Stop' | 'Restart' | 'Deallocate';

interface VmDto {
  id: string;
  name: string;
  resourceGroup: string;
  subscriptionId: string;
  subscriptionName: string;
  location: string;
  vmSize: string;
  vCpuCount: number;
  memoryGb: number;
  osType: string;
  powerState: VmPowerState;
  privateIpAddress?: string;
  publicIpAddress?: string;
  tags: Record<string, string>;
}

interface VmMetricsDto {
  vmId: string;
  cpuPercent: number;
  memoryPercent: number;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const STATE_COLOR: Record<VmPowerState, string> = {
  Running: '#28a745',
  Stopped: '#ffc107',
  Deallocated: '#6c757d',
  Starting: '#17a2b8',
  Stopping: '#fd7e14',
  Deallocating: '#fd7e14',
  Unknown: '#6c757d',
};

const STATE_BG: Record<VmPowerState, string> = {
  Running: '#28a74514',
  Stopped: '#ffc10714',
  Deallocated: '#6c757d14',
  Starting: '#17a2b814',
  Stopping: '#fd7e1414',
  Deallocating: '#fd7e1414',
  Unknown: '#6c757d14',
};

const STATE_DOT: Record<VmPowerState, string> = {
  Running: '🟢',
  Stopped: '🟡',
  Deallocated: '⚪',
  Starting: '🔵',
  Stopping: '🟠',
  Deallocating: '🟠',
  Unknown: '❓',
};

const ACTION_LABELS: Record<VmPowerAction, string> = {
  Start: 'Start',
  Stop: 'Stop',
  Restart: 'Restart',
  Deallocate: 'Deallocate',
};

function MiniBar({ value, color }: { value: number; color: string }) {
  const v = Math.min(Math.max(value, 0), 100);
  const barColor = v > 85 ? '#dc3545' : v > 65 ? '#ffc107' : color;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{ flex: 1, height: 5, background: '#333', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: `${v}%`, height: '100%', background: barColor, borderRadius: 3, transition: 'width 0.4s' }} />
      </div>
      <span style={{ fontSize: '0.75rem', color: '#b0b0b0', minWidth: 32, textAlign: 'right' }}>{v.toFixed(0)}%</span>
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

const VmDashboard: React.FC = () => {
  const { showToast } = useToast();
  const { user } = useAuth();
  // Mirrors the API policy: VM power actions need operator or admin.
  const canWrite = user?.role === 'operator' || user?.role === 'admin';
  const [vms, setVms] = useState<VmDto[]>([]);
  const [metrics, setMetrics] = useState<Record<string, VmMetricsDto>>({});
  const [loading, setLoading] = useState(true);
  const [actionTarget, setActionTarget] = useState<{ vmId: string; action: VmPowerAction } | null>(null);
  const [actioning, setActioning] = useState(false);
  const [search, setSearch] = useState('');
  const [filterState, setFilterState] = useState<VmPowerState | ''>('');
  const [filterSub, setFilterSub] = useState('');
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadVms = useCallback(async () => {
    try {
      const [vmsRes, metricsRes] = await Promise.all([
        axios.get(`${API_URL}/vm`, { params: { subscriptionId: 'all' } }),
        axios.get(`${API_URL}/vm/metrics`, { params: { subscriptionId: 'all' } }).catch(() => ({ data: [] })),
      ]);
      setVms(vmsRes.data);
      const map: Record<string, VmMetricsDto> = {};
      for (const m of metricsRes.data) map[m.vmId] = m;
      setMetrics(map);
      setLastRefresh(new Date());
    } catch (e) {
      showToast(extractErrorMessage(e, 'Failed to load VMs.'), 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadVms();
    intervalRef.current = setInterval(loadVms, 30000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [loadVms]);

  const runAction = async (vmId: string, action: VmPowerAction) => {
    setActioning(true);
    try {
      await axios.post(`${API_URL}/vm/power`, { vmIds: [vmId], action, dryRun: false });
      showToast(`${action} sent to ${vms.find(v => v.id === vmId)?.name ?? vmId}.`, 'success');
      setTimeout(loadVms, 1500);
    } catch (e) {
      showToast(extractErrorMessage(e, `${action} failed.`), 'error');
    } finally {
      setActioning(false);
      setActionTarget(null);
    }
  };

  // ── Derived data ─────────────────────────────────────────────────────────────

  const subscriptions = Array.from(new Set(vms.map(v => v.subscriptionName))).sort();

  const filtered = vms.filter(v => {
    if (filterState && v.powerState !== filterState) return false;
    if (filterSub && v.subscriptionName !== filterSub) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!v.name.toLowerCase().includes(q) && !v.resourceGroup.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const counts: Partial<Record<VmPowerState, number>> = {};
  for (const v of vms) counts[v.powerState] = (counts[v.powerState] ?? 0) + 1;

  const summaryCards: { state: VmPowerState; label: string }[] = [
    { state: 'Running', label: 'Running' },
    { state: 'Stopped', label: 'Stopped' },
    { state: 'Deallocated', label: 'Deallocated' },
  ];

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h2 style={{ margin: 0 }}>VM Health Dashboard</h2>
          <p style={{ margin: '0.25rem 0 0', color: '#888', fontSize: '0.875rem' }}>
            Live status board — refreshes every 30s &nbsp;·&nbsp; Last: {lastRefresh.toLocaleTimeString()}
          </p>
        </div>
        <button
          onClick={loadVms}
          style={{ background: '#25252a', border: '1px solid #555', color: '#f0f0f0', borderRadius: 6, padding: '0.4rem 1rem', cursor: 'pointer', fontSize: '0.875rem' }}
        >
          ↺ Refresh
        </button>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
        <div
          onClick={() => setFilterState('')}
          style={{
            background: filterState === '' ? '#007bff18' : '#25252a',
            border: `1px solid ${filterState === '' ? '#007bff' : '#444'}`,
            borderRadius: 8, padding: '0.75rem 1.25rem', cursor: 'pointer', minWidth: 100,
          }}
        >
          <div style={{ fontSize: '1.75rem', fontWeight: 700 }}>{vms.length}</div>
          <div style={{ color: '#888', fontSize: '0.825rem' }}>All VMs</div>
        </div>
        {summaryCards.map(({ state, label }) => (
          <div
            key={state}
            onClick={() => setFilterState(f => f === state ? '' : state)}
            style={{
              background: filterState === state ? STATE_COLOR[state] + '22' : '#25252a',
              border: `1px solid ${filterState === state ? STATE_COLOR[state] : '#444'}`,
              borderRadius: 8, padding: '0.75rem 1.25rem', cursor: 'pointer', minWidth: 100,
            }}
          >
            <div style={{ fontSize: '1.75rem', fontWeight: 700, color: STATE_COLOR[state] }}>{counts[state] ?? 0}</div>
            <div style={{ color: '#888', fontSize: '0.825rem' }}>{label}</div>
          </div>
        ))}
        {/* Alert: stopped but not deallocated = still billing */}
        {(counts['Stopped'] ?? 0) > 0 && (
          <div style={{ background: '#ffc10714', border: '1px solid #ffc10755', borderRadius: 8, padding: '0.75rem 1.25rem', minWidth: 200, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: '1.2rem' }}>⚠️</span>
            <div>
              <div style={{ color: '#ffc107', fontWeight: 600, fontSize: '0.875rem' }}>
                {counts['Stopped']} VM{(counts['Stopped'] ?? 0) > 1 ? 's' : ''} Stopped (still billing)
              </div>
              <div style={{ color: '#888', fontSize: '0.775rem' }}>Deallocate to stop charges</div>
            </div>
          </div>
        )}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.25rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          placeholder="Search by name or resource group…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ background: '#1a1a1e', color: '#f0f0f0', border: '1px solid #444', borderRadius: 5, padding: '0.4rem 0.75rem', fontSize: '0.875rem', minWidth: 260 }}
        />
        <select
          value={filterSub}
          onChange={e => setFilterSub(e.target.value)}
          style={{ background: '#1a1a1e', color: '#f0f0f0', border: '1px solid #444', borderRadius: 5, padding: '0.4rem 0.65rem', fontSize: '0.875rem' }}
        >
          <option value="">All Subscriptions</option>
          {subscriptions.map(s => <option key={s}>{s}</option>)}
        </select>
        <span style={{ color: '#666', fontSize: '0.825rem', marginLeft: 'auto' }}>{filtered.length} VM{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {/* VM Grid */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>Loading VMs…</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>No VMs match the current filters.</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem' }}>
          {filtered.map(vm => {
            const m = metrics[vm.id];
            const isTransient = ['Starting', 'Stopping', 'Deallocating'].includes(vm.powerState);
            return (
              <div
                key={vm.id}
                style={{
                  background: STATE_BG[vm.powerState],
                  border: `1px solid ${STATE_COLOR[vm.powerState]}44`,
                  borderRadius: 10,
                  padding: '1rem',
                  position: 'relative',
                  transition: 'border-color 0.2s',
                }}
              >
                {/* State dot + name */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
                  <span style={{ fontSize: '1rem', lineHeight: 1.4 }}>{STATE_DOT[vm.powerState]}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: '0.95rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={vm.name}>
                      {vm.name}
                    </div>
                    <div style={{ color: '#888', fontSize: '0.775rem', marginTop: 1 }}>
                      {vm.resourceGroup} · {vm.location}
                    </div>
                  </div>
                  <span style={{
                    background: STATE_COLOR[vm.powerState] + '33',
                    color: STATE_COLOR[vm.powerState],
                    border: `1px solid ${STATE_COLOR[vm.powerState]}55`,
                    borderRadius: 10, padding: '0.15rem 0.5rem',
                    fontSize: '0.7rem', fontWeight: 600, whiteSpace: 'nowrap',
                  }}>
                    {isTransient ? '⟳ ' : ''}{vm.powerState}
                  </span>
                </div>

                {/* Spec row */}
                <div style={{ display: 'flex', gap: 8, fontSize: '0.775rem', color: '#888', marginBottom: 8, flexWrap: 'wrap' }}>
                  <span>{vm.vmSize}</span>
                  <span>·</span>
                  <span>{vm.osType}</span>
                  {vm.privateIpAddress && <><span>·</span><span>{vm.privateIpAddress}</span></>}
                </div>

                {/* Metrics bars — only if VM is running and metrics available */}
                {vm.powerState === 'Running' && m && (
                  <div style={{ marginBottom: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: '0.7rem', color: '#888', minWidth: 30 }}>CPU</span>
                      <div style={{ flex: 1 }}><MiniBar value={m.cpuPercent} color="#007bff" /></div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: '0.7rem', color: '#888', minWidth: 30 }}>MEM</span>
                      <div style={{ flex: 1 }}><MiniBar value={m.memoryPercent} color="#6f42c1" /></div>
                    </div>
                  </div>
                )}

                {/* Subscription pill */}
                <div style={{ fontSize: '0.72rem', color: '#666', marginBottom: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {vm.subscriptionName}
                </div>

                {/* Action buttons */}
                {actionTarget?.vmId === vm.id ? (
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '0.8rem', color: '#b0b0b0', alignSelf: 'center', marginRight: 4 }}>Run:</span>
                    {(['Start', 'Stop', 'Restart', 'Deallocate'] as VmPowerAction[])
                      .filter(a => {
                        if (vm.powerState === 'Running') return a !== 'Start';
                        if (vm.powerState === 'Stopped') return a === 'Start' || a === 'Deallocate';
                        if (vm.powerState === 'Deallocated') return a === 'Start';
                        return false;
                      })
                      .map(a => (
                        <button
                          key={a}
                          disabled={actioning}
                          onClick={() => runAction(vm.id, a)}
                          style={{
                            background: a === 'Start' ? '#28a74522' : a === 'Stop' ? '#ffc10722' : a === 'Restart' ? '#17a2b822' : '#6c757d22',
                            color: a === 'Start' ? '#28a745' : a === 'Stop' ? '#ffc107' : a === 'Restart' ? '#17a2b8' : '#aaa',
                            border: `1px solid currentColor`, borderRadius: 4,
                            padding: '0.2rem 0.5rem', cursor: 'pointer', fontSize: '0.775rem', fontWeight: 600,
                            opacity: actioning ? 0.5 : 1,
                          }}
                        >
                          {ACTION_LABELS[a]}
                        </button>
                      ))}
                    <button
                      onClick={() => setActionTarget(null)}
                      style={{ background: 'transparent', border: '1px solid #555', color: '#888', borderRadius: 4, padding: '0.2rem 0.4rem', cursor: 'pointer', fontSize: '0.75rem' }}
                    >✕</button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => setActionTarget({ vmId: vm.id, action: 'Start' })}
                      disabled={isTransient || !canWrite}
                      title={canWrite ? undefined : 'Requires the operator role'}
                      style={{
                        background: '#25252a', border: '1px solid #555', color: '#b0b0b0',
                        borderRadius: 5, padding: '0.25rem 0.65rem', cursor: isTransient ? 'not-allowed' : 'pointer',
                        fontSize: '0.8rem', opacity: isTransient ? 0.4 : 1,
                      }}
                    >
                      ⚡ Action
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default VmDashboard;
